import React from 'react';
import Container from '../../common/Container';
import { Heading, Subheading } from '../../common/TextComponents';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

const EmployerAdvantageSection = ({ isDarkMode = false }) => {
  return (
    <div className={`py-20 md:py-24 ${
      isDarkMode 
        ? 'bg-gradient-to-br from-gray-800 via-gray-900 to-gray-800' 
        : 'bg-gradient-to-br from-gray-50 via-white to-gray-50'
    }`}>
      <Container>
        <div className="text-center mb-16">
          <Subheading dark={isDarkMode}>Comparison</Subheading>
          <Heading as="h2" className="mt-2 mb-6" dark={isDarkMode}>
            The Burnt Advantage Over Traditional Hiring
          </Heading>
          <p className={`mx-auto max-w-3xl text-xl ${
            isDarkMode ? 'text-gray-300' : 'text-gray-600'
          }`}>
            Discover how Burnt transforms the hiring experience for high-impact roles.
          </p>
        </div>

        <div className="mt-12 pb-16 md:pb-20">
          <div className={`mx-auto max-w-5xl rounded-2xl shadow-2xl overflow-hidden ${
            isDarkMode 
              ? 'bg-gray-800/90 backdrop-blur-sm ring-1 ring-gray-700/50' 
              : 'bg-white/90 backdrop-blur-sm ring-1 ring-gray-200/50'
          }`}>
            {/* Header */}
            <div className="grid grid-cols-3 text-center">
              <div className={`py-6 px-4 border-b border-r ${
                isDarkMode ? 'border-gray-600' : 'border-gray-200'
              }`}>
                <span className={`text-lg font-semibold ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Hiring Method
                </span>
              </div>
              <div className={`py-6 px-4 border-b border-r ${
                isDarkMode 
                  ? 'border-gray-600 bg-purple-500/10' 
                  : 'border-gray-200 bg-purple-50'
              }`}>
                <span className={`text-lg font-bold ${
                  isDarkMode ? 'text-purple-300' : 'text-purple-600'
                }`}>
                  Burnt
                </span>
              </div>
              <div className={`py-6 px-4 border-b ${
                isDarkMode ? 'border-gray-600' : 'border-gray-200'
              }`}>
                <span className={`text-lg font-semibold ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Traditional Hiring
                </span>
              </div>
            </div>

            {/* Rows */}
            <div className="grid grid-cols-3 text-center items-stretch">
              {/* Candidate Quality Row */}
              <div className={`p-6 border-b border-r flex items-center justify-center ${
                isDarkMode ? 'border-gray-600' : 'border-gray-200'
              }`}>
                <span className={`font-semibold text-base ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  Candidate Quality
                </span>
              </div>
              <div className={`p-6 border-b border-r flex flex-col items-center justify-center ${
                isDarkMode 
                  ? 'border-gray-600 bg-purple-500/5' 
                  : 'border-gray-200 bg-purple-50'
              }`}>
                <CheckIcon className="h-6 w-6 text-green-500 mb-3" />
                <span className={`text-sm leading-relaxed ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Pre-vetted professionals with transferable skills beyond resume bullet points
                </span>
              </div>
              <div className={`p-6 border-b flex flex-col items-center justify-center ${
                isDarkMode ? 'border-gray-600' : 'border-gray-200'
              }`}>
                <XMarkIcon className="h-6 w-6 text-red-500 mb-3" />
                <span className={`text-sm leading-relaxed ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Variable quality, often dependent on keyword matching
                </span>
              </div>

              {/* Access to Talent Row */}
              <div className={`p-6 border-b border-r flex items-center justify-center ${
                isDarkMode ? 'border-gray-600' : 'border-gray-200'
              }`}>
                <span className={`font-semibold text-base ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  Access to Talent
                </span>
              </div>
              <div className={`p-6 border-b border-r flex flex-col items-center justify-center ${
                isDarkMode 
                  ? 'border-gray-600 bg-purple-500/5' 
                  : 'border-gray-200 bg-purple-50'
              }`}>
                <CheckIcon className="h-6 w-6 text-green-500 mb-3" />
                <span className={`text-sm leading-relaxed ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Direct access to motivated candidates seeking meaningful change
                </span>
              </div>
              <div className={`p-6 border-b flex flex-col items-center justify-center ${
                isDarkMode ? 'border-gray-600' : 'border-gray-200'
              }`}>
                <XMarkIcon className="h-6 w-6 text-red-500 mb-3" />
                <span className={`text-sm leading-relaxed ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Limited to active job seekers or those responsive to recruiters
                </span>
              </div>

              {/* Candidate Insights Row */}
              <div className={`p-6 border-b border-r flex items-center justify-center ${
                isDarkMode ? 'border-gray-600' : 'border-gray-200'
              }`}>
                <span className={`font-semibold text-base ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  Candidate Insights
                </span>
              </div>
              <div className={`p-6 border-b border-r flex flex-col items-center justify-center ${
                isDarkMode 
                  ? 'border-gray-600 bg-purple-500/5' 
                  : 'border-gray-200 bg-purple-50'
              }`}>
                <CheckIcon className="h-6 w-6 text-green-500 mb-3" />
                <span className={`text-sm leading-relaxed ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Deep understanding of motivation, burnout context, and desired next step
                </span>
              </div>
              <div className={`p-6 border-b flex flex-col items-center justify-center ${
                isDarkMode ? 'border-gray-600' : 'border-gray-200'
              }`}>
                <XMarkIcon className="h-6 w-6 text-red-500 mb-3" />
                <span className={`text-sm leading-relaxed ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Surface-level information about past roles and responsibilities
                </span>
              </div>

              {/* Time to Hire Row */}
              <div className={`p-6 border-r flex items-center justify-center ${
                isDarkMode ? 'border-gray-600' : 'border-gray-200'
              }`}>
                <span className={`font-semibold text-base ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  Time to Hire
                </span>
              </div>
              <div className={`p-6 border-r flex flex-col items-center justify-center ${
                isDarkMode 
                  ? 'border-gray-600 bg-purple-500/5' 
                  : 'border-gray-200 bg-purple-50'
              }`}>
                <CheckIcon className="h-6 w-6 text-green-500 mb-3" />
                <span className={`text-sm leading-relaxed ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Days to weeks with direct candidate access
                </span>
              </div>
              <div className="p-6 flex flex-col items-center justify-center">
                <XMarkIcon className="h-6 w-6 text-red-500 mb-3" />
                <span className={`text-sm leading-relaxed ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Weeks to months through multiple screening layers
                </span>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default EmployerAdvantageSection;
